import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { ProductCollectionEditor } from './ProductCollectionEditor';
import {
  COLLECTION_MALLS,
  COLLECTION_MALL_LABELS,
  DEFAULT_COLLECTION_MALL,
  type CollectionMall,
} from './collectionMalls';
import type { ProductCollectionConfig } from './types';
import { CollectionTemplateApplyBar } from '../collectionTemplates/CollectionTemplateApplyBar';

/** 몰별 추가입력·동의 설정 맵. 각 몰이 독립된 폼(번역 아님). */
export type ProductCollectionByMall = Record<CollectionMall, ProductCollectionConfig>;

export interface ProductCollectionMallTabsProps {
  /** 몰별 설정 맵(4몰 모두 키 존재 — 빈 설정은 {fields:[],consents:[]}). */
  value: ProductCollectionByMall;
  disabled?: boolean;
  /** 활성 몰의 설정 변경. 호스트가 해당 몰 슬롯만 교체한다. */
  onChange: (mall: CollectionMall, next: ProductCollectionConfig) => void;
  /** QA-563 [템플릿 불러오기] 막대 — 상품 폼은 켜고, 템플릿 편집 모달은 끈다. 기본 true. */
  withTemplateBar?: boolean;
}

/**
 * 항목2 — 상품 추가입력·동의 4몰 탭(국내/일문/중문/영문).
 *
 * be-catalog per-mall 계약(`?mall=<BCP-47>`)에 대응해, 각 몰을 **독립된 필드세트**로
 * 편집한다(LocaleHtmlTabs 의 base+translations 폴백 모델과 달리, 몰별로 완전히 별개의
 * 추가입력·동의 항목을 둔다). 탭 전환은 클라이언트 state 만 바꾸고, 실제 per-mall
 * GET seed / PUT 저장은 호스트(ProductForm·Create/Edit 페이지)가 담당한다.
 *
 * 백오피스 UI 는 한국어지만 탭 라벨은 몰 구분 표기(국내몰/일문몰/중문몰/영문몰)를 쓴다.
 */
export const ProductCollectionMallTabs = ({
  value,
  disabled,
  onChange,
  withTemplateBar = true,
}: ProductCollectionMallTabsProps) => {
  const [activeMall, setActiveMall] = useState<CollectionMall>(DEFAULT_COLLECTION_MALL);

  // QA-507 — 개인정보·배송일정·미성년자 등 공통 동의는 위 [공통 동의]에서 고른다(종전 "동의 템플릿 넣기"는 공통과 겹쳐 뺐다).
  return (
    <>
    {/* QA-563 추가입력·동의 템플릿 — 4몰 항목을 한 번에. 템플릿 관리 화면(모달)에서는 끈다. */}
    {withTemplateBar ? (
      <CollectionTemplateApplyBar value={value} {...(disabled !== undefined ? { disabled } : {})} onChange={onChange} />
    ) : null}
    <Tabs
      value={activeMall}
      onValueChange={(v) => setActiveMall(v as CollectionMall)}
      className="w-full"
    >
      <TabsList className="mb-4">
        {COLLECTION_MALLS.map((mall) => (
          <TabsTrigger key={mall} value={mall} data-testid={`collection-mall-tab-${mall}`}>
            {COLLECTION_MALL_LABELS[mall]}
          </TabsTrigger>
        ))}
      </TabsList>

      {COLLECTION_MALLS.map((mall) => (
        <TabsContent key={mall} value={mall}>
          <p className="mb-3 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {COLLECTION_MALL_LABELS[mall]}에만 적용되는 추가입력·동의 항목입니다. 몰별로 별도
            구성됩니다.
          </p>
          <ProductCollectionEditor
            value={value[mall]}
            {...(disabled !== undefined ? { disabled } : {})}
            onChange={(next) => onChange(mall, next)}
          />
        </TabsContent>
      ))}
    </Tabs>
    </>
  );
};

/** 빈 4몰 맵 — 신규 상품 기본값. */
export const emptyCollectionByMall = (): ProductCollectionByMall =>
  COLLECTION_MALLS.reduce((acc, mall) => {
    acc[mall] = { fields: [], consents: [] };
    return acc;
  }, {} as ProductCollectionByMall);
